/* eslint-disable no-magic-numbers */
import { useRef, useCallback } from 'react';

import { UnionDragEvent } from './useNativeDropZone';

export interface TreeDragDropHandlers {
  dragOverItemCurrent: number | undefined;
  onDragStart: (index: number) => void;
  onDragOver: (event: UnionDragEvent, index: number, rowElement?: HTMLElement) => void;
  onDragLeave: (event: UnionDragEvent, rowElement?: HTMLElement) => void;
  onDrop: (event: UnionDragEvent) => void;
  onDragEnd: () => void;
  onMoveUp: (index: number) => void;
  onMoveDown: (index: number) => void;
  onMoveToTop: (index: number) => void;
  onMoveToBottom: (index: number) => void;
  canMoveUp: (index: number) => boolean;
  canMoveDown: (index: number) => boolean;
}

interface UseTreeDragDropParams<T> {
  dataLength: number;
  isDragInvalid: (dragIndex: number, dropIndex: number) => boolean;
  calculateNewTree: (dragIndex: number, dropIndex: number, direction?: 'up' | 'down') => T | undefined;
  onDragComplete: (tree: T) => void;
  onManualMoveComplete?: (tree: T) => void;
  canManualMove?: boolean;
}

/**
 * Generic drag-and-drop hook for tree-based reordering.
 * Encapsulates the common handler logic (start, over, leave, drop, end, move up/down/top/bottom)
 * while delegating validation and tree calculation to the caller.
 */
export const useTreeDragDrop = <T>({
  dataLength,
  isDragInvalid,
  calculateNewTree,
  onDragComplete,
  onManualMoveComplete,
  canManualMove = true
}: UseTreeDragDropParams<T>): TreeDragDropHandlers => {
  const dragItem = useRef<number | undefined>();
  const dragOverItem = useRef<number | undefined>();

  const onDragStart = useCallback((index: number) => {
    dragItem.current = index;
  }, []);

  const onDragOver = useCallback(
    (event: UnionDragEvent, index: number, rowElement?: HTMLElement) => {
      event.preventDefault();

      if (typeof dragItem.current !== 'number') {
        return;
      }

      dragOverItem.current = index;

      if (isDragInvalid(dragItem.current, index)) {
        return;
      }

      if (rowElement) {
        rowElement.classList.add('drag-over-active');
      }
    },
    [isDragInvalid]
  );

  const onDragLeave = useCallback((_event: UnionDragEvent, rowElement?: HTMLElement) => {
    if (rowElement) {
      rowElement.classList.remove('drag-over-active');
    }
  }, []);

  const onDrop = useCallback(
    (event: UnionDragEvent) => {
      event.preventDefault();
      document.querySelectorAll('.drag-over-active').forEach(el => el.classList.remove('drag-over-active'));

      if (typeof dragItem.current !== 'number' || typeof dragOverItem.current !== 'number') {
        return;
      }

      const newTree = calculateNewTree(dragItem.current, dragOverItem.current);
      if (newTree) {
        onDragComplete(newTree);
      }
    },
    [calculateNewTree, onDragComplete]
  );

  const onDragEnd = useCallback(() => {
    dragItem.current = undefined;
    dragOverItem.current = undefined;
    document.querySelectorAll('.drag-over-active').forEach(el => el.classList.remove('drag-over-active'));
  }, []);

  const moveHandler = onManualMoveComplete || onDragComplete;

  const onMoveUp = useCallback(
    (index: number) => {
      if (!canManualMove) {
        return;
      }
      for (let i = index - 1; i >= 0; i--) {
        if (!isDragInvalid(index, i)) {
          const newTree = calculateNewTree(index, i, 'up');
          if (newTree) {
            moveHandler(newTree);
          }
          break;
        }
      }
    },
    [canManualMove, isDragInvalid, calculateNewTree, moveHandler]
  );

  const onMoveDown = useCallback(
    (index: number) => {
      if (!canManualMove) {
        return;
      }
      for (let i = index + 1; i < dataLength; i++) {
        if (!isDragInvalid(index, i)) {
          const newTree = calculateNewTree(index, i, 'down');
          if (newTree) {
            moveHandler(newTree);
          }
          break;
        }
      }
    },
    [canManualMove, isDragInvalid, calculateNewTree, moveHandler, dataLength]
  );

  const onMoveToTop = useCallback(
    (index: number) => {
      if (!canManualMove) {
        return;
      }
      for (let i = 0; i < index; i++) {
        if (!isDragInvalid(index, i)) {
          const newTree = calculateNewTree(index, i, 'up');
          if (newTree) {
            moveHandler(newTree);
          }
          break;
        }
      }
    },
    [canManualMove, isDragInvalid, calculateNewTree, moveHandler]
  );

  const onMoveToBottom = useCallback(
    (index: number) => {
      if (!canManualMove) {
        return;
      }
      for (let i = dataLength - 1; i > index; i--) {
        if (!isDragInvalid(index, i)) {
          const newTree = calculateNewTree(index, i, 'down');
          if (newTree) {
            moveHandler(newTree);
          }
          break;
        }
      }
    },
    [canManualMove, isDragInvalid, calculateNewTree, moveHandler, dataLength]
  );

  const canMoveUp = useCallback(
    (index: number) => {
      for (let i = index - 1; i >= 0; i--) {
        if (!isDragInvalid(index, i)) {
          return true;
        }
      }
      return false;
    },
    [isDragInvalid]
  );

  const canMoveDown = useCallback(
    (index: number) => {
      for (let i = index + 1; i < dataLength; i++) {
        if (!isDragInvalid(index, i)) {
          return true;
        }
      }
      return false;
    },
    [isDragInvalid, dataLength]
  );

  return {
    dragOverItemCurrent: dragOverItem.current,
    onDragStart,
    onDragOver,
    onDragLeave,
    onDrop,
    onDragEnd,
    onMoveUp,
    onMoveDown,
    onMoveToTop,
    onMoveToBottom,
    canMoveUp,
    canMoveDown
  };
};
